import React, { useState } from "react";
import {
  CONNECT_SOURCES_COMING_SOON,
  CONNECT_SOURCES_LIVE,
} from "./connectSourcesCatalog";
import ConnectSourceCatalogIcon from "./ConnectSourceCatalogIcon";

const CalendarPreviewIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="17" rx="2" />
    <path d="M3 9h18M8 2v4M16 2v4" />
  </svg>
);

const LockTrustIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    aria-hidden="true"
  >
    <rect x="4" y="10" width="16" height="10" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </svg>
);

/**
 * Onboarding-style empty state for connecting meeting sources.
 *
 * @example
 * <ConnectSourcesEmptyState
 *   variant="web"
 *   onConnectApp={(app) => navigate(`/business/settings?tab=connectors&app=${app.id}`)}
 * />
 *
 * @example
 * <ConnectSourcesEmptyState
 *   variant="extension"
 *   showTrustCard={false}
 *   onConnectApp={(app) => startSetup(app.workflowType, app.appName)}
 * />
 */
const ConnectSourcesEmptyState = ({
  variant = "web",
  layout = "default",
  headline = "Connect your sources",
  subheadline = "",
  showPreviewHint = true,
  showTrustCard = true,
  showComingSoon = true,
  connectedWorkflowTypes = [],
  onConnectApp,
  onRequestSource,
  escapeAction = null,
  footerCta = null,
  collapseComingSoon = true,
  onGoToTasks = null,
  liveApps = CONNECT_SOURCES_LIVE,
  comingSoonApps = CONNECT_SOURCES_COMING_SOON,
}) => {
  const [showTrustModal, setShowTrustModal] = useState(false);
  const connectedSet = new Set(
    (Array.isArray(connectedWorkflowTypes) ? connectedWorkflowTypes : [])
      .map((value) => String(value || "").trim())
      .filter(Boolean)
  );

  const comingSoonCount = comingSoonApps.length;
  const showComingSoonSection = showComingSoon && comingSoonCount > 0;
  const footerLabel = String(footerCta?.label || "").trim();
  const showFooterCta =
    footerLabel && typeof footerCta?.onClick === "function";

  const rootClass = [
    "connect-sources",
    variant === "extension" ? "connect-sources--extension" : "",
    layout === "embedded" ? "connect-sources--embedded" : "",
    layout === "onboarding" ? "connect-sources--onboarding" : "",
    showFooterCta ? "connect-sources--with-footer" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClass}>
      <div className="connect-sources__intro">
        <h2 className="connect-sources__headline">{headline}</h2>
        {showPreviewHint ? (
          <p className="connect-sources__preview">
            <CalendarPreviewIcon />
            <span>Includes action items from the last 30 days</span>
          </p>
        ) : null}
      </div>

      <p className="connect-sources__group-label">Available now</p>
      <div className="connect-sources__cards" role="list">
        {liveApps.map((app) => {
          const connected = connectedSet.has(app.workflowType);
          return (
            <article key={app.id} className="connect-sources__card" role="listitem" data-connect-app-id={app.id}>
              <div className={`connect-sources__card-icon business-catalog-app-icon ${app.iconClass}`}>
                <ConnectSourceCatalogIcon source={app} />
              </div>
              <h3 className="connect-sources__card-name">{app.appName}</h3>
              <p className="connect-sources__card-desc">{app.description}</p>
              <div className="connect-sources__card-action">
                {connected ? (
                  <span className="connect-sources__connected" aria-label={`${app.appName} connected`}>
                    <span className="connect-sources__connected-check" aria-hidden="true">
                      ✓
                    </span>
                    Connected
                  </span>
                ) : (
                  <button
                    type="button"
                    className="connect-sources__connect-btn"
                    onClick={() => onConnectApp?.(app)}
                  >
                    Connect
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {showComingSoonSection ? (
        <>
          <p className="connect-sources__group-label">More sources</p>
          <div className="connect-sources__cards connect-sources__cards--soon" role="list">
            {comingSoonApps.map((app) => (
              <article key={app.id} className="connect-sources__card connect-sources__card--soon" role="listitem">
                <div className={`connect-sources__card-icon business-catalog-app-icon ${app.iconClass || ""}`}>
                  <ConnectSourceCatalogIcon source={app} />
                </div>
                <h3 className="connect-sources__card-name">{app.appName}</h3>
                <div className="connect-sources__card-action">
                  <button
                    type="button"
                    className="connect-sources__request-btn"
                    onClick={() => onRequestSource?.(app)}
                  >
                    Request
                  </button>
                </div>
              </article>
            ))}
          </div>
        </>
      ) : null}

      {showTrustCard ? (
        <div className="connect-sources__trust">
          <div className="connect-sources__trust-title">
            <LockTrustIcon />
            <span>Your data stays yours</span>
          </div>
          <div className="connect-sources__trust-cols">
            <div className="connect-sources__trust-col">
              <h4>Accessed</h4>
              <ul>
                <li>
                  <span className="connect-sources__trust-yes" aria-hidden="true">
                    ✓
                  </span>
                  Meeting transcripts &amp; summaries
                </li>
                <li>
                  <span className="connect-sources__trust-yes" aria-hidden="true">
                    ✓
                  </span>
                  The action items inside them
                </li>
              </ul>
            </div>
            <div className="connect-sources__trust-col">
              <h4>Not accessed</h4>
              <ul>
                <li>
                  <span className="connect-sources__trust-no" aria-hidden="true">
                    ✕
                  </span>
                  Read your full inbox
                </li>
                <li>
                  <span className="connect-sources__trust-no" aria-hidden="true">
                    ✕
                  </span>
                  Send or change anything
                </li>
                <li>
                  <span className="connect-sources__trust-no" aria-hidden="true">
                    ✕
                  </span>
                  Sell your data or train on it
                </li>
              </ul>
            </div>
          </div>
          <div className="connect-sources__trust-badges">
            <span className="connect-sources__trust-badge">Read-only access</span>
            <span className="connect-sources__trust-badge">Encrypted in transit &amp; at rest</span>
          </div>
        </div>
      ) : null}

      {(!showTrustCard && !showFooterCta) || typeof onGoToTasks === "function" ? (
        <div className="connect-sources__footer-bar">
          {typeof onGoToTasks === "function" ? (
            <button
              type="button"
              className="connect-sources__go-to-tasks-btn"
              onClick={onGoToTasks}
            >
              Go to action items →
            </button>
          ) : null}
          {!showTrustCard && !showFooterCta ? (
            <button
              type="button"
              className="connect-sources__footer-trust-link"
              onClick={() => setShowTrustModal(true)}
            >
              <LockTrustIcon />
              <span>Learn how your data is handled</span>
            </button>
          ) : null}
        </div>
      ) : null}

      {escapeAction?.label && typeof escapeAction.onClick === "function" ? (
        <p className="connect-sources__escape">
          <button type="button" className="connect-sources__escape-btn" onClick={escapeAction.onClick}>
            {escapeAction.label}
          </button>
        </p>
      ) : null}

      {showFooterCta ? (
        <div className="connect-sources__footer">
          <div className="connect-sources__footer-inner">
            <button
              type="button"
              className="connect-sources__footer-cta"
              disabled={Boolean(footerCta.disabled)}
              onClick={footerCta.onClick}
            >
              {footerLabel}
            </button>
            <button
              type="button"
              className="connect-sources__footer-trust-link"
              onClick={() => setShowTrustModal(true)}
            >
              <LockTrustIcon />
              <span>Learn how your data is handled</span>
            </button>
          </div>
        </div>
      ) : null}

      {showTrustModal ? (
        <div
          className="connect-sources__trust-overlay"
          onClick={() => setShowTrustModal(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Data privacy details"
        >
          <div
            className="connect-sources__trust-popup"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="connect-sources__trust-popup-close"
              onClick={() => setShowTrustModal(false)}
              aria-label="Close"
            >
              ×
            </button>
            <div className="connect-sources__trust-title">
              <LockTrustIcon />
              <span>Your data stays yours</span>
            </div>
            <div className="connect-sources__trust-cols">
              <div className="connect-sources__trust-col">
                <h4>Accessed</h4>
                <ul>
                  <li><span className="connect-sources__trust-yes" aria-hidden="true">✓</span> Meeting transcripts &amp; summaries</li>
                  <li><span className="connect-sources__trust-yes" aria-hidden="true">✓</span> The action items inside them</li>
                </ul>
              </div>
              <div className="connect-sources__trust-col">
                <h4>Not accessed</h4>
                <ul>
                  <li><span className="connect-sources__trust-no" aria-hidden="true">✕</span> Read your full inbox</li>
                  <li><span className="connect-sources__trust-no" aria-hidden="true">✕</span> Send or change anything</li>
                  <li><span className="connect-sources__trust-no" aria-hidden="true">✕</span> Sell your data or train on it</li>
                </ul>
              </div>
            </div>
            <div className="connect-sources__trust-badges">
              <span className="connect-sources__trust-badge">Read-only access</span>
              <span className="connect-sources__trust-badge">Encrypted in transit &amp; at rest</span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ConnectSourcesEmptyState;
